import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { doc, updateDoc, Timestamp } from 'firebase/firestore'
import { db } from '../../lib/firebase'

import { Check, Crown, Zap } from 'lucide-react'

export default function Billing() {
  const { currentUser, userProfile, fetchUserProfile } = useAuth()
  const [billingCycle, setBillingCycle] = useState('yearly')
  const [loading, setLoading] = useState(false)

  // Plan prices in INR
  const planPrices = {
    starter: { monthly: 9, yearly: 99 },
    pro: { monthly: 29, yearly: 199 },
    business: { monthly: 99, yearly: 499 }
  }

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: billingCycle === 'monthly' ? '₹9' : '₹99',
      period: billingCycle === 'monthly' ? '/month' : '/year',
      description: 'Perfect for individuals',
      features: [
        '1 Digital vCard',
        '1 Template',
        'QR Code Download',
        'Basic Analytics',
        'Unlimited Sharing'
      ],
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: billingCycle === 'monthly' ? '₹29' : '₹199',
      period: billingCycle === 'monthly' ? '/month' : '/year',
      description: 'Most Popular Choice',
      features: [
        'All templates & themes',
        'Unlimited updates',
        'Advanced analytics',
        'Custom username',
        'Services & portfolio section',
        'Priority support'
      ],
      popular: true
    },
    {
      id: 'business',
      name: 'Business',
      price: billingCycle === 'monthly' ? '₹99' : '₹499',
      period: billingCycle === 'monthly' ? '/month' : '/year',
      description: 'For small teams',
      features: [
        'Up to 5 vCards',
        'Remove branding',
        'Employee profiles',
        'Lead capture form',
        'Team dashboard',
        'Premium support'
      ],
      popular: false
    }
  ]

  async function handleSubscribe(planId) {
    const razorpayKeyId = import.meta.env.VITE_RAZORPAY_KEY_ID
    const apiUrl = import.meta.env.VITE_API_URL
    
    if (!razorpayKeyId) {
      alert('Razorpay is not configured. Please add VITE_RAZORPAY_KEY_ID to your .env file.')
      return
    }

    if (!apiUrl) {
      alert('API URL is not configured. Please add VITE_API_URL to your .env file.')
      return
    }

    setLoading(true)

    try {
      // Get price based on plan and billing cycle
      const amount = planPrices[planId][billingCycle]

      // Create order via Cloudflare Worker
      const orderRes = await fetch(`${apiUrl}/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          receipt: `vc_${Date.now()}`,
          notes: {
            userId: currentUser.uid,
            plan: planId,
            cycle: billingCycle
          }
        })
      })

      const order = await orderRes.json()
      console.log('Order response:', order)

      if (order.error || !order.orderId) {
        throw new Error(order.error || order.hint || 'Failed to create order')
      }

      // Open Razorpay Checkout
      const options = {
        key: razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Digital vCard',
        description: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan - ${billingCycle}`,
        order_id: order.orderId,
        handler: async function(response) {
          // Payment successful - verify and update plan
          try {
            // Verify payment via Cloudflare Worker
            const verifyRes = await fetch(`${apiUrl}/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                userId: currentUser.uid
              })
            })

            const verification = await verifyRes.json()

            if (verification.success) {
              // Update user plan in Firestore
              const expiresAt = new Date()
              if (billingCycle === 'monthly') {
                expiresAt.setMonth(expiresAt.getMonth() + 1)
              } else {
                expiresAt.setFullYear(expiresAt.getFullYear() + 1)
              }

              await updateDoc(doc(db, 'users', currentUser.uid), {
                plan: planId,
                expiresAt: Timestamp.fromDate(expiresAt),
                paymentId: response.razorpay_payment_id
              })

              // Refresh user profile
              await fetchUserProfile(currentUser.uid)
              
              alert('🎉 Payment successful! Your plan has been upgraded.')
            }
          } catch (error) {
            console.error('Verification error:', error)
            alert('Payment received but verification failed. Please contact support.')
          }
        },
        prefill: {
          name: userProfile?.name || '',
          email: currentUser?.email || ''
        },
        theme: {
          color: '#00A6FB'
        }
      }

      const razorpay = new window.Razorpay(options)
      razorpay.open()

    } catch (error) {
      console.error('Payment error:', error)
      alert('Failed to initiate payment. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="billing-page">
      <div className="page-header">
        <h1 className="page-title">Billing & Plans</h1>
        <p className="page-subtitle">Choose the perfect plan for your needs</p>
      </div>

      {/* Current Plan */}
      <div className="dashboard-card current-plan-card" style={{ marginBottom: 'var(--space-xl)' }}>
        <div className="current-plan-header">
          <div>
            <span className="text-sm text-muted">Current Plan</span>
            <h3 className="current-plan-name">
              {userProfile?.plan === 'free' ? 'Free Plan' : 'Pro Plan'}
            </h3>
          </div>
          {userProfile?.plan !== 'free' && userProfile?.expiresAt && (
            <div className="plan-expires">
              <span>Renews on</span>
              <strong>{new Date(userProfile.expiresAt.toDate()).toLocaleDateString()}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Billing Toggle */}
      <div className="billing-toggle-container">
        <div className="billing-toggle">
          <button 
            className={billingCycle === 'monthly' ? 'active' : ''}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button 
            className={billingCycle === 'yearly' ? 'active' : ''}
            onClick={() => setBillingCycle('yearly')}
          >
            Yearly
            <span className="save-badge">Save 20%</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="billing-plans-grid">
        {plans.map((plan) => (
          <div key={plan.id} className={`billing-plan-card ${plan.popular ? 'popular' : ''}`}>
            {plan.popular && (
              <div className="popular-badge">
                <Crown size={14} />
                Most Popular
              </div>
            )}
            
            <h3 className="plan-name">{plan.name}</h3>
            <p className="plan-description">{plan.description}</p>
            
            <div className="plan-price">
              <span className="price">{plan.price}</span>
              <span className="period">{plan.period}</span>
            </div>

            <ul className="plan-features">
              {plan.features.map((feature, index) => (
                <li key={index}>
                  <Check size={18} />
                  {feature}
                </li>
              ))}
            </ul>

            <button 
              className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
              style={{ width: '100%' }}
              onClick={() => handleSubscribe(plan.id)}
              disabled={loading}
            >
              {loading ? (
                <span className="spinner"></span>
              ) : (
                <>
                  <Zap size={18} />
                  {userProfile?.plan === plan.id ? 'Current Plan' : 'Upgrade Now'}
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div className="dashboard-card" style={{ marginTop: 'var(--space-xl)' }}>
        <h3 className="dashboard-card-title">Frequently Asked Questions</h3>
        <div className="billing-faq">
          <div className="faq-item-simple">
            <h4>Can I cancel anytime?</h4>
            <p>We accept all major credit/debit cards, UPI, and net banking through Razorpay.</p>
          </div>
          <div className="faq-item-simple">
            <h4>Is there a refund policy?</h4>
            <p>Yes, we offer a 7-day money-back guarantee if you're not satisfied with our service.</p>
          </div>
        </div>
      </div>

      <style>{`
        .current-plan-card {
          background: linear-gradient(135deg, var(--bg-primary), var(--bg-secondary));
          color: white;
        }
        
        .current-plan-header {
          display: flex;
          justify-content: space-between;
          font-size: var(--font-size-xl);
          font-weight: 600;
          margin-top: var(--space-xs);
        }
        
        .plan-expires {
          text-align: right;
        }
        
        .plan-expires span {
          display: block;
          font-size: var(--font-size-sm);
          opacity: 0.7;
        }
        
        .billing-toggle-container {
          display: flex;
          justify-content: center;
          margin-bottom: var(--space-xl);
        }
        
        .billing-toggle {
          display: flex;
          gap: var(--space-sm);
          background: var(--gray-100);
          padding: 6px;
          border-radius: var(--radius-full);
        }
        
        .billing-toggle button {
          padding: 12px 24px;
          background: transparent;
          color: var(--text-body);
          border-radius: var(--radius-full);
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          transition: all var(--transition-fast);
        }
        
        .billing-toggle button.active {
          background: white;
          color: var(--text-dark);
          box-shadow: var(--shadow-sm);
        }
        
        .save-badge {
          background: var(--success);
          color: white;
          padding: 2px 8px;
          border-radius: var(--radius-full);
          font-size: var(--font-size-xs);
        }
        
        .billing-plans-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-lg);
        }
        
        @media (max-width: 1100px) {
          .billing-plans-grid {
            grid-template-columns: 1fr;
            max-width: 400px;
            margin: 0 auto;
          }
        }
        
        .billing-plan-card {
          background: white;
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-xl);
          padding: var(--space-xl);
          position: relative;
          transition: transform var(--transition-base), box-shadow var(--transition-base);
        }
        
        .billing-plan-card:hover {
          transform: translateY(-5px);
          box-shadow: var(--shadow-lg);
        }
        
        .billing-plan-card.popular {
          border-color: var(--primary);
          box-shadow: 0 0 0 1px var(--primary);
        }
        
        .popular-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          color: white;
          padding: 6px 16px;
          border-radius: var(--radius-full);
          font-size: var(--font-size-xs);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .plan-name {
          font-size: var(--font-size-xl);
          font-weight: 600;
          margin-bottom: var(--space-xs);
        }
        
        .plan-description {
          color: var(--text-muted);
          font-size: var(--font-size-sm);
          margin-bottom: var(--space-md);
        }
        
        .plan-price {
          margin-bottom: var(--space-lg);
        }
        
        .plan-price .price {
          font-size: 40px;
          font-weight: 700;
        }
        
        .plan-price .period {
          font-size: var(--font-size-base);
          color: var(--text-muted);
        }
        
        .plan-features {
          margin-bottom: var(--space-xl);
        }
        
        .plan-features li {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm) 0;
          font-size: var(--font-size-sm);
        }
        
        .plan-features li svg {
          color: var(--success);
          flex-shrink: 0;
        }
        
        .billing-faq {
          display: flex;
          flex-direction: column;
          gap: var(--space-lg);
        }
        
        .faq-item-simple h4 {
          font-size: var(--font-size-base);
          font-weight: 600;
          margin-bottom: var(--space-xs);
        }
        
        .faq-item-simple p {
          color: var(--text-muted);
          font-size: var(--font-size-sm);
        }
      `}</style>
    </div>
  )
}
